<template>
  <div class="w-full" :style="indentStyle">
    <div class="panel flex flex-col w-full" :class="levelClass">
      <!-- Header: chevron + name + predicted end -->
      <div class="flex flex-row w-full items-center">
        <button
          v-if="canExpand"
          type="button"
          class="flex items-center justify-center w-6 h-6 mr-2 text-blue cursor-pointer focus:outline-none"
          @click="toggleExpand"
        >
          <b-icon
            :icon="isExpanded ? 'chevron-down' : 'chevron-right'"
            pack="fas"
            size="is-small"
          />
        </button>
        <div v-else-if="showSpacer" class="w-6 h-6 mr-2"></div>

        <div class="flex-1 text-blue font-bold">
          {{ chartTitlePrefix }}{{ group.name }}
        </div>

        <div v-if="predictedEndMonth && showPredictedEndDate" class="text-xs text-right pr-2" style="color: orange;">
          {{ $t('expected_end_date_actual_performance') }}: {{ formatDate(predictedEndMonth) }}
        </div>
        <div v-if="predictionAccelerated && showPredictedEndDate" class="text-xs text-right pr-2" style="color: orange;">
          {{ $t('prediction_spi_accelerated') }}
        </div>
      </div>

      <!-- Charts row -->
      <div class="flex flex-row w-full mt-2">
        <!-- Main chart -->
        <div :class="showCurrentMonth ? 'w-3/4 flex flex-col' : 'w-full flex flex-col'">
          <div v-if="isPending" class="w-full relative flex flex-row text-center" style="height: 80px;">
            <b-loading :is-full-page="false" :active="true" :can-cancel="false" />
          </div>
          <line-chart
            v-else
            :data="mainChartData"
            :colors="mainChartColors"
            :suffix="suffix"
            :min="min"
            :max="max"
            :library="mainChartLibrary"
          />

          <!-- tablita al pie con resumen de meses -->
          <div v-if="!isPending && chartTableMonths.length" class="group-progress-chart-table-wrapper mt-2">
            <table class="group-progress-chart-table">
              <thead>
                <tr>
                  <th class="group-progress-chart-table__label"></th>
                  <th v-for="month in chartTableMonths" :key="'month_' + month" class="group-progress-chart-table__month">
                    {{ formatChartTableMonthHeader(month) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in chartTableRows" :key="'row_' + row.name">
                  <td class="group-progress-chart-table__label">
                    <span class="group-progress-chart-table__legend" :style="{ backgroundColor: row.color }"></span>
                    {{ row.name }}
                  </td>
                  <td v-for="month in chartTableMonths" :key="'cell_' + row.name + '_' + month"
                    class="group-progress-chart-table__value">
                    {{ formatChartTableValue(row.values[month]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Optional secondary chart (current month detail) -->
        <div v-if="showCurrentMonth" class="w-1/4 flex flex-col">
          <div class="flex flex-row w-full text-blue font-bold">{{ $t('current_month') }}</div>
          <div v-if="isPending" class="w-full relative flex flex-row text-center" style="height: 80px;">
            <b-loading :is-full-page="false" :active="true" :can-cancel="false" />
          </div>
          <line-chart
            v-else
            :data="currentMonthChartData"
            :colors="currentMonthChartColors"
            :suffix="suffix"
            :min="min"
            :max="max"
          />
        </div>
      </div>
    </div>

    <!-- Children (recursive) -->
    <b-collapse v-if="canExpand" :open.sync="isExpanded" animation="slide" :aria-id="collapseId">
      <div class="w-full mt-2">
        <div v-if="isLoadingChildren" class="w-full text-center py-4">
          <scale-loader />
        </div>
        <div v-else-if="childrenLoaded && children.length === 0" class="text-center text-xs text-grey-dark py-2">
          {{ $t('no_results') }}
        </div>
        <group-progress-chart
          v-for="child in children"
          :key="'group_' + child.id"
          :group="child"
          :level="level + 1"
          :max-depth="maxDepth"
          :main-chart-colors="mainChartColors"
          :main-chart-library="mainChartLibrary"
          :current-month-chart-colors="currentMonthChartColors"
          :show-current-month="showCurrentMonth"
          :show-predicted-end-date="showPredictedEndDate"
          :fetch-children="fetchChildren"
          :suffix="suffix"
          :min="min"
          :max="max"
        />
      </div>
    </b-collapse>
  </div>
</template>

<script>
import moment from 'moment'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'

const INDENT_PX_PER_LEVEL = 24
const PRECEDING_TITLE = ''

export default {
  name: 'group-progress-chart',

  components: { ScaleLoader },

  props: {
    group: { type: Object, required: true },
    level: { type: Number, default: 0 },
    maxDepth: { type: Number, default: 10 },

    mainChartColors: { type: Array, default: () => [] },
    mainChartLibrary: { type: Object, default: () => ({ spanGaps: true }) },
    currentMonthChartColors: { type: Array, default: () => ['#DC3913', '#129618'] },

    showCurrentMonth: { type: Boolean, default: false },
    showPredictedEndDate: { type: Boolean, default: false },
    showTitlePrefix: { type: Boolean, default: true },

    suffix: { type: String, default: '%' },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },

    // (parentId) => Promise<Array<group>>
    fetchChildren: { type: Function, default: null },
  },

  data() {
    return {
      isExpanded: false,
      children: [],
      childrenLoaded: false,
      isLoadingChildren: false,
    }
  },

  computed: {
    collapseId() {
      return 'group_collapse_' + this.group.id
    },

    chartTitlePrefix() {
      return this.level === 0 && this.showTitlePrefix ? this.$t('progress_of') + ' ' : PRECEDING_TITLE
    },

    indentStyle() {
      return this.level > 0 ? { paddingLeft: (this.level * INDENT_PX_PER_LEVEL) + 'px' } : {}
    },

    levelClass() {
      return this.level === 0 ? '' : 'bg-grey-lightest'
    },

    mainChartData() {
      return this.group.dataset != null ? this.group.dataset : (this.group.data || [])
    },

    chartSeries() {
      return (Array.isArray(this.mainChartData) ? this.mainChartData : [])
        .filter(series => series && series.name && series.data && typeof series.data === 'object')
    },

    chartTableMonths() {
      const monthKeys = new Set()

      this.chartSeries.forEach(series => {
        Object.keys(series.data).forEach(dateKey => {
          if (this.isChartMonthEndDate(dateKey)) {
            monthKeys.add(dateKey)
          }
        })
      })

      return Array.from(monthKeys).sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
    },

    chartTableRows() {
      return this.chartSeries.map((series, index) => {
        const values = {}

        this.chartTableMonths.forEach(month => {
          const rawValue = series.data[month]
          values[month] = rawValue == null ? null : Number(rawValue)
        })

        return {
          name: series.name,
          color: this.mainChartColors[index] || '#284ec0',
          values,
        }
      })
    },

    currentMonthChartData() {
      return this.group.datasetCurrentMonth != null
        ? this.group.datasetCurrentMonth
        : (this.group.dataCurrentMonth || [])
    },

    predictedEndMonth() {
      return this.group.predictedEndMonth
    },

    predictionAccelerated() {
      return this.group.predictionAccelerated === true
    },

    isPending() {
      return this.group.pendingLoad === true
    },

    hasChildren() {
      return this.group.has_children === true
    },

    canExpand() {
      return this.hasChildren
        && typeof this.fetchChildren === 'function'
        && this.level < this.maxDepth
    },

    showSpacer() {
      return this.level === 0 && !this.canExpand
    },
  },

  methods: {
    async toggleExpand() {
      if (!this.canExpand) return

      const willOpen = !this.isExpanded
      this.isExpanded = willOpen

      if (willOpen && !this.childrenLoaded && !this.isLoadingChildren) {
        await this.loadChildren()
      }
    },

    async loadChildren() {
      this.isLoadingChildren = true
      try {
        const result = await this.fetchChildren(this.group.id)
        this.children = Array.isArray(result) ? result : []
        this.childrenLoaded = true
      } catch (error) {
        console.log('Error loading children of group ' + this.group.id, error)
        this.$notify.error('error_retrieving_data')
        this.children = []
      } finally {
        this.isLoadingChildren = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return null
      const [year, month, day] = String(dateString).split('-')
      return day + '-' + month + '-' + year
    },

    isChartMonthEndDate(dateKey) {
      const date = moment(dateKey, 'YYYY-MM-DD', true)
      if (!date.isValid()) return false
      return date.date() === date.clone().endOf('month').date()
    },

    formatChartTableMonthHeader(dateKey) {
      return moment(dateKey, 'YYYY-MM-DD').format('M/YY')
    },

    formatChartTableValue(value) {
      if (value == null || Number.isNaN(value)) return ''
      return Math.round(value).toLocaleString('de-DE') + this.suffix // miles con punto
    },
  },
}
</script>

<style scoped>
.group-progress-chart-table-wrapper {
  width: 100%;
}

.group-progress-chart-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.6rem;
  line-height: 1.1;
}

.group-progress-chart-table th,
.group-progress-chart-table td {
  border: 1px solid #dbdbdb;
  padding: 0.1rem 0.05rem;
}

.group-progress-chart-table__label {
  width: 10%;
  max-width: 6rem;
  text-align: left;
  white-space: normal;
  word-break: break-word;
  vertical-align: middle;
  padding-left: 0.25rem;
  padding-right: 0.15rem;
}

.group-progress-chart-table__month {
  width: auto;
  min-width: 0;
  text-align: center;
  vertical-align: bottom;
  padding: 0.15rem 0;
  font-weight: normal;
  /* writing-mode: vertical-rl; */
  /* transform: rotate(180deg); */
  white-space: nowrap;
  line-height: 1;
}

.group-progress-chart-table__value {
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-progress-chart-table__legend {
  display: inline-block;
  width: 0.75rem;
  height: 0.15rem;
  margin-right: 0.2rem;
  vertical-align: middle;
  flex-shrink: 0;
}
</style>
